import { Inject, Injectable } from '@angular/core';
import { DwConfigService, DwConfigServiceToken, DwEventService, DwOrmDataService, DwOrmDataServiceToken, DwSecurityUserService } from '@devwareapps/devware-cap';
import { combineLatest, defer, Observable, of, throwError } from 'rxjs';
import { catchError, map, shareReplay, switchMap, take } from 'rxjs/operators';
import { AppMetaData, UserProfileEntity, UserChallengeEntity, DwUserEntity, ChallengeTeamEntity } from '../../../../meta-data/app-meta-data.service';
import { canonicalSlug } from './profile-links';
import { AppearanceSettings } from '../appearance/appearance';

import { CentralDataService } from '../../../streaker-central/services/central-data.service';

export type DirectoryUser = { resolvedDisplay?: boolean; _itemName?: string } & AppearanceSettings & Pick<UserProfileEntity, 'UserProfileId' | 'UserId' | 'ProfileSlug' | 'UserDescription'> & Pick<DwUserEntity, 'FirstName' | 'LastName' | 'UserPictureMediaId' | 'UserBackgroundMediaId'> & Pick<UserChallengeEntity, 'UserChallengeId' | 'ChallengeDefinitionId'>;
export interface ProfileDirectory { users: DirectoryUser[]; teams: ChallengeTeamEntity[]; }
/** Session-only public projections. CAP still applies metadata and row security. */
@Injectable({ providedIn: 'root' })
export class ProfileDirectoryService {
    private entries = new Map<string, { expires: number; stream: Observable<ProfileDirectory> }>();
    constructor(@Inject(DwOrmDataServiceToken) private orm: DwOrmDataService, private security: DwSecurityUserService,
        @Inject(DwConfigServiceToken) private config: DwConfigService, events: DwEventService, private data: CentralDataService) {
        security.securityContextChanged().subscribe(() => this.invalidate());
        data.teamChanged$.subscribe(() => this.invalidate());
        data.changed$.subscribe(() => this.invalidate());
        [AppMetaData.DwUser, AppMetaData.UserProfile, AppMetaData.UserChallenge, AppMetaData.ChallengeTeam, AppMetaData.ChallengeTeamMember].map(metadata => metadata.ItemDetail.itemName).forEach(item =>
            events.getEvent(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(item)).subscribe(() => this.invalidate()));
    }
    invalidate(): void { this.entries.clear(); }
    user(slug: string, challengeId: number): Observable<DirectoryUser> {
        return defer(() => {
            const canonical = canonicalSlug(slug);
            return this.get(challengeId, 'streaker').pipe(switchMap(directory => {
                const found = directory.users.filter(user => user.ProfileSlug === canonical);
                if (found.length === 1) { return of(found[0]); }
                if (found.length > 1) { throw new Error('Profile link is not unique.'); }
                return this.data.profileBySlug(canonical).pipe(map(rows => {
                    if (rows.length !== 1) { throw new Error('Profile unavailable.'); }
                    return rows[0];
                }));
            }));
        });
    }
    get(challengeId: number, subject?: 'streaker' | 'team'): Observable<ProfileDirectory> {
        return defer(() => {
            if (!Number.isSafeInteger(challengeId) || challengeId <= 0) { throw new Error('Select a challenge.'); }
            // Use the existing account-scoped snapshots without waiting for the
            // other directory before displaying a single profile's identity.
            if (subject === 'streaker') { return this.data.streakers(challengeId).pipe(map(users => ({users, teams: []}))); }
            if (subject === 'team') { return this.data.publicTeams(challengeId).pipe(map(teams => ({users: [], teams}))); }
            const context = this.security.securityContext;
            const key = JSON.stringify([this.config.coreConfig.apiRoot, context?.ApiRoot,
                context?.AuthenticatedUser?.UserId, context?.ApplicationUser?.UserId, challengeId]), cached = this.entries.get(key);
            if (cached && cached.expires > Date.now()) { return cached.stream; }
            const stream = combineLatest([this.data.streakers(challengeId), this.data.publicTeams(challengeId)]).pipe(
                map(([users, publicTeams]) => ({ users, teams: publicTeams })),
                catchError(error => { if (this.entries.get(key)?.stream === stream) { this.entries.delete(key); } return throwError(error); }),
                shareReplay({ bufferSize: 1, refCount: true }));
            this.entries.set(key, { expires: Date.now() + 5 * 60 * 1000, stream });
            return stream;
        });
    }
}
